export const workExperience = [
  {
    company: "Technical University of Munich",
    position: "Master's Thesis",
    duration: {
      start: "July 2024",
      end: "January 2025",
      length: "7 months"
    },
    summary: [
      "Research work on Variational Quantum Circuits (VQC) and their use in data-driven optimization tasks"
    ],
    responsibilities: [
      "Developed quantum machine learning models for contextual bandit problems",
      "Compared quantum-based and classical regression models",
      "Investigated the effects of hyperparameters on model performance"
    ],
    results: [
      "Demonstrated the potential of quantum-based methods in the optimization of contextual bandit problems"
    ],
    technologies: ["Quantum Machine Learning", "Contextual Bandits", "Reinforcement Learning", "Python", "Qiskit", "Pennylane", "TensorFlow", "PyTorch"],
    logo: "/images/logos/tum-logo.png"
  },
  {
    company: "Noxon GmbH",
    position: "Software Project Assistant",
    duration: {
      start: "October 2023",
      end: "March 2024",
      length: "6 months"
    },
    summary: [
      "Assisting with the development of an AI-based tremor detection system using AI and advanced data analysis.",
      "Integrated sensors into an arm sleeve to capture EMG and IMU data."
    ],
    responsibilities: [
      "Worked on the project \"Holistic System for Tremor Detection using AI and Data Analysis\" to develop a proof-of-concept wearable system for tremor detection in Parkinson's disease patients.",
      "Designed and implemented a wearable hardware system featuring an ESP32 Feather V2, MPU-6050, and an EMG amplifier to collect and transmit sensor data.",
      "Programmed the ESP32 (using C++/Arduino and PlatformIO) to pre-process sensor data with low-pass, high-pass, and notch filters, batch data for efficient WebSocket transmission, and synchronize timestamps via NTP.",
      "Developed a robust backend server using Django, Django Channels, SQLite, and Uvicorn to receive, process, and store real-time data.",
      "Created a responsive web application with React to visualize data and provide user interaction, establishing a baseline for future tremor suppression products."
    ],
    results: [
      "Successfully established a proof-of-concept product that accurately detects tremors in real time, providing a solid foundation for future wearable sensor technologies.",
      "Optimized data sampling and transmission processes to address challenges such as high-frequency data collection, power consumption, and network resilience.",
      "Delivered a scalable, interdisciplinary solution that bridges hardware, backend processing, and frontend visualization for enhanced patient monitoring."
    ],
    technologies: ["ESP32", "C++", "Arduino", "PlatformIO", "Django", "React", "WebSocket", "SQLite", "Uvicorn"],
    logo: "/images/logos/noxon-logo.png"
  },
  {
    company: "Technical University of Munich",
    position: "Bachelor's Thesis",
    duration: {
      start: "December 2019",
      end: "March 2020",
      length: "4 months"
    },
    summary: [
      "Research work on sparse grid density estimation with the combination technique"
    ],
    responsibilities: [
      "Implemented sparse grid density estimation using the combination technique within the sparseSpACE framework",
      "Optimized grid-based density estimation techniques to mitigate the curse of dimensionality",
      "Investigated the usage of mass lumping in the density estimation process"
    ],
    results: [
      "Density estimation with the combination technique was implemented and successfully integrated into the sparseSpACE framework",
      "Accelerated density estimation for high-dimensional datasets"
    ],
    technologies: ["Scientific Computing", "Density Estimation", "Sparse Grids", "Python", "SG++"],
    logo: "/images/logos/tum-logo.png"
  },
  {
    company: "Shanghai Electrical Apparatus Research Institute",
    position: "Internship",
    duration: {
      start: "June 2015",
      end: "June 2015",
      length: "1 month"
    },
    summary: [
      "Internship focusing on smart home systems and IoT integration"
    ],
    responsibilities: [
      "Designed an IoT-based smart home system for controlling various devices",
      "Developed an Android app for remote control of IoT devices"
    ],
    results: [
      "Simplified device management through centralized control",
      "Enhanced user experience with a mobile application"
    ],
    technologies: ["Android", "Java", "IoT", "ZigBee", "MQTT", "Embedded Systems"],
    logo: "/images/logos/seari-logo.png"
  },
  {
    company: "Terex Material Handling & Port Solutions",
    position: "Internship",
    duration: {
      start: "January 2012",
      end: "January 2012",
      length: "1 month"
    },
    summary: [
      "Internship in the metal and electrical department, gaining insights into industrial manufacturing processes."
    ],
    responsibilities: [
      "Activities included filing, drilling, milling, turning, soldering, and working with pneumatic controls."
    ],
    results: [
      "Gained solid hands-on experience in manufacturing and assembly processes."
    ],
    technologies: ["Industrial Machinery", "Electrical Circuits", "Manufacturing Tools"],
    logo: "/images/logos/terex-logo.png"
  }
];

export const protectedFiles = [
    {
      title: "Variational Quantum Circuits in Offline Contextual Bandit Problems",
      type: "Research Paper",
      date: "2025",
      description: "Research paper exploring the application of variational quantum circuits for solving data-based contextual bandit problems in industrial optimization tasks.",
      abstract: "This paper explores the application of variational quantum circuits (VQCs) for solving offline contextual bandit problems in industrial optimization tasks. Using the Industrial Benchmark (IB) environment, we evaluate the performance of quantum regression models against classical models. Our findings demonstrate that quantum models can effectively fit complex reward functions, identify optimal configurations via particle swarm optimization (PSO), and generalize well in noisy and sparse datasets. These results provide a proof of concept for utilizing VQCs in offline contextual bandit problems and highlight their potential in industrial optimization tasks.",
      tags: ["Quantum Computing", "Machine Learning", "Industrial Applications", "Optimization"],
      note: "Available upon request",
      filename: "QCE25_paper_347.pdf",
      isPubliclyAvailable: false // Change to true if you want to make the file publicly available
    },
    {
      title: "Optimization of Data-Based Contextual Bandits Using Variational Quantum Circuits",
      type: "Academic Work",
      date: "2025",
      description: "Master thesis investigating the application of variational quantum circuits as regression models for solving data-based contextual bandit problems in industrial optimization.",
      abstract: "This thesis investigates the application of variational quantum circuits as regression models for solving data-based contextual bandit problems. A simulation environment that models complex industrial processes is used to collect a dataset of interactions with the environment, forming the basis for the offline contextual bandit problem. Both quantum and classical regression models are trained to predict the cost of machine settings given a context and subsequently used to identify superior operating points of the industrial process using particle swarm optimization to search in the continuous parameter space.",
      tags: ["Quantum Computing", "Machine Learning", "Optimization", "Industrial Applications"],
      note: "Available upon request",
      filename: "masters-thesis.pdf",
      isPubliclyAvailable: false // Change to true if you want to make the file publicly available
    },
    {
      title: "Sparse Grid Density Estimation with the Combination Technique",
      type: "Academic Work",
      date: "2020",
      description: "Bachelor thesis implementing density estimation with sparse grid combination technique in the sparseSpACE framework.",
      abstract: "In the frame of this bachelor thesis, density estimation with the sparse grid combination technique was implemented and integrated into the sparseSpACE framework. The sparse grid combination technique is used to compute a sparse grid, whereby a specific sequence of small anisotropic full grids is combined linearly, to tackle the curse of dimensionality. The usage of mass lumping in the density estimation process is also explored, which still achieves relatively good results compared to the standard combination method.",
      tags: ["Numerical Methods", "Density Estimation", "Sparse Grids", "High-Dimensional Data"],
      note: "Available upon request",
      filename: "bachelors-thesis.pdf",
      isPubliclyAvailable: false // Change to true if you want to make the file publicly available
    },
    {
      title: "Data Mining in Science",
      type: "Seminar Paper",
      date: "2018",
      description: "A comprehensive overview of data mining applications across various scientific domains including astronomy, bioinformatics, economy, healthcare, and meteorology.",
      abstract: "Data mining is becoming increasingly important due to the large amount of data that is generated in almost all areas of life today. For this reason, researchers have been working on ways to apply data mining techniques to scientific problems in order to deal with the vast amount of data. In this article, the role of data mining in science is discussed and applications in astronomy, bioinformatics, economy, healthcare and meteorology are presented.",
      tags: ["Data Mining", "Scientific Applications", "Machine Learning", "Big Data"],
      note: "Available upon request",
      filename: "data_mining_science_paper.pdf",
      isPubliclyAvailable: false // Change to true if you want to make the file publicly available
    }
  ];
